import { clientApi, serverApi, handleApiError } from "./index";

import type {
  User,
  LoginRequest,
  LoginResponse,
  PaginatedResponse,
  PaginationParams,
} from "./types";

// 사용자 로그인
export const loginUser = async (credentials: LoginRequest): Promise<LoginResponse> => {
  try {
    const response = await clientApi.post<LoginResponse>("api/auth/login", credentials);
    return response;
  } catch (error) {
    const apiError = handleApiError(error);
    throw new Error(`Login failed: ${apiError.message}`);
  }
};

// 사용자 로그아웃
export const logoutUser = async (): Promise<void> => {
  try {
    await clientApi.post("api/auth/logout");
  } catch (error) {
    const apiError = handleApiError(error);
    throw new Error(`Logout failed: ${apiError.message}`);
  }
};

// 현재 사용자 정보 가져오기 (클라이언트용)
export const getCurrentUser = async (): Promise<User> => {
  try {
    const user = await clientApi.get<User>("api/user/me", { authorization: true });
    return user;
  } catch (error) {
    const apiError = handleApiError(error);
    throw new Error(`Failed to get current user: ${apiError.message}`);
  }
};

// 현재 사용자 정보 가져오기 (서버용)
export const getCurrentUserServer = async (): Promise<User> => {
  try {
    const user = await serverApi.get<User>("api/user/me", { authorization: true });
    return user;
  } catch (error) {
    const apiError = handleApiError(error);
    throw new Error(`Failed to get current user: ${apiError.message}`);
  }
};

// 사용자 정보 업데이트
export const updateUser = async (userId: string, updates: Partial<User>): Promise<User> => {
  try {
    const user = await clientApi.put<User>(`api/user/${userId}`, updates, { authorization: true });
    return user;
  } catch (error) {
    const apiError = handleApiError(error);
    throw new Error(`Failed to update user: ${apiError.message}`);
  }
};

// 사용자 목록 가져오기 (페이지네이션)
export const getUsers = async (params: PaginationParams): Promise<PaginatedResponse<User>> => {
  try {
    const queryParams = new URLSearchParams({
      page: params.page.toString(),
      limit: params.limit.toString(),
      ...(params.sortBy && { sortBy: params.sortBy }),
      ...(params.sortOrder && { sortOrder: params.sortOrder }),
    });

    const response = await clientApi.get<PaginatedResponse<User>>(`api/users?${queryParams}`, {
      authorization: true,
    });
    return response;
  } catch (error) {
    const apiError = handleApiError(error);
    throw new Error(`Failed to get users: ${apiError.message}`);
  }
};

// 특정 사용자 정보 가져오기
export const getUserById = async (userId: string): Promise<User> => {
  try {
    const user = await clientApi.get<User>(`api/user/${userId}`, { authorization: true });
    return user;
  } catch (error) {
    const apiError = handleApiError(error);
    throw new Error(`Failed to get user: ${apiError.message}`);
  }
};

// 사용자 삭제
export const deleteUser = async (userId: string): Promise<void> => {
  try {
    await clientApi.delete(`api/user/${userId}`, { authorization: true });
  } catch (error) {
    const apiError = handleApiError(error);
    throw new Error(`Failed to delete user: ${apiError.message}`);
  }
};

// 사용자 권한 변경
export const updateUserRole = async (userId: string, role: string): Promise<User> => {
  try {
    const user = await clientApi.put<User>(
      `api/user/${userId}/role`,
      { role },
      { authorization: true }
    );
    return user;
  } catch (error) {
    const apiError = handleApiError(error);
    throw new Error(`Failed to update user role: ${apiError.message}`);
  }
};

// 사용자 API 캐시 키
export const USER_QUERY_KEYS = {
  all: ["users"] as const,
  lists: () => [...USER_QUERY_KEYS.all, "list"] as const,
  list: (params: PaginationParams) => [...USER_QUERY_KEYS.lists(), params] as const,
  details: () => [...USER_QUERY_KEYS.all, "detail"] as const,
  detail: (id: string) => [...USER_QUERY_KEYS.details(), id] as const,
  current: () => [...USER_QUERY_KEYS.all, "current"] as const,
} as const;
